import type { ProjectQueryRequest, QueryNode } from "@secretaa/contracts-data";
import { formatMinor, separatorsOf } from "@secretaa/ui";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { labelOf, type MCollection } from "../data/manifest-view.ts";
import { type AliasView } from "./query-draft.ts";
import { pathLabel } from "./query-filter.tsx";

export function QueryResults({ rows, request, collections, aliases, base, selected, onSelect }: { rows: QueryNode[]; request: ProjectQueryRequest; collections: readonly MCollection[]; aliases: AliasView[]; base: string; selected: string[]; onSelect: (ids: string[]) => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const sep = separatorsOf(lang);
  const render = (node: QueryNode, alias = "root", root = true) => {
    const c = collections.find(c => c.id === node.source.collectionId);
    if (!c) return null;
    const target = `${base}/${c.key}/${node.source.recordId}`;
    return <article key={node.source.recordId} aria-label={t("projectQuery.recordLabel", { collection: labelOf(c.label, lang), id: node.source.recordId })} className="space-y-3 rounded-md border border-(--saa-border) p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {root ? <label className="flex min-w-0 items-center gap-2 break-all text-sm"><input type="checkbox" checked={selected.includes(node.source.recordId)} onChange={e => onSelect(e.target.checked ? [...selected, node.source.recordId] : selected.filter(id => id !== node.source.recordId))} />{t("projectQuery.selectRecord", { id: node.source.recordId })}</label> : null}
        <Link className="text-(--saa-primary) underline" to={target}>{t("projectQuery.openRecord", { collection: labelOf(c.label, lang) })}</Link>
        <span className="text-xs text-(--saa-fg-muted)">{t("projectQuery.version", { version: node.source.recordVersion })}</span>
      </div>
      <dl className="grid gap-2 sm:grid-cols-2">
        {node.cells.map(cell => {
          const fieldId = request.select.find(s => s.pathAlias === alias && s.as === cell.as)?.fieldId;
          const f = c.fields.find(f => f.id === fieldId);
          if (!f) return null;
          const value = cell.value;
          let display: string = t("projectQuery.missing");
          if (value) {
            if (value.type === "money") display = `${formatMinor(value.minor, value.scale, sep)} ${value.currency}`;
            else if (value.type === "date") display = new Intl.DateTimeFormat(lang, { dateStyle: "medium", timeZone: "UTC" }).format(new Date(`${value.value}T00:00:00Z`));
            else if (value.type === "bool") display = value.value ? t("common.yes") : t("common.no");
            else if (value.type === "select") {
              const option = (f.config as { options: { id: string; label: { vi: string; en: string } }[] }).options.find(o => o.id === value.value);
              display = option ? labelOf(option.label, lang) : t("projectQuery.missing");
            } else if (value.type === "number") { const fraction = value.value.split(".")[1] ?? ""; display = formatMinor(value.value.replace(".", ""), fraction.length, sep); }
            else display = value.value;
          }
          return <div key={cell.as} className="min-w-0"><dt className="text-xs text-(--saa-fg-muted)">{labelOf(f.label, lang)}</dt><dd className="break-words text-sm">{display}</dd>{cell.source ? <dd><Link className="text-xs text-(--saa-primary) underline" to={target}>{t("projectQuery.source", { field: labelOf(f.label, lang), version: cell.source.recordVersion })}</Link></dd> : null}</div>;
        })}
      </dl>
      {Object.entries(node.relations).map(([childAlias, child]) => {
        const a = aliases.find(a => a.alias === childAlias);
        if (!a) return null;
        return <section key={childAlias} aria-label={pathLabel(a, aliases, request.paths, lang)} className="space-y-2 border-l-2 border-(--saa-border) pl-3">
          <h4 className="text-sm font-medium">{pathLabel(a, aliases, request.paths, lang)}</h4>
          {child === null ? <p className="text-sm">{t("projectQuery.missingRelation")}</p> : Array.isArray(child) ? child.length ? child.map(n => render(n, childAlias, false)) : <p className="text-sm">{t("projectQuery.emptyRelation")}</p> : render(child, childAlias, false)}
        </section>;
      })}
    </article>;
  };
  const renderRoot = (row: QueryNode) => render(row);
  return <div className="space-y-3">{rows.map(renderRoot)}</div>;
}
